import {USER_KEYS} from "@/api/userApi";
import CircleWithTickIcon from "@/assets/colorable/circle_with_tick.svg?react";
import CircleWithTimesIcon from "@/assets/colorable/circle_with_times.svg?react";
import FullPageLoader from "@/components/Common/FullPageLoader";
import {client} from "@/httpClient";
import {useMutation} from "@tanstack/react-query";
import {createFileRoute} from "@tanstack/react-router";
import {zodValidator} from "@tanstack/zod-adapter";
import {useTranslation} from "react-i18next";
import {useEffectOnce, useTitle} from "react-use";
import {z} from "zod";

export const Route = createFileRoute("/verify-email-change")({
  component: VerifyEmailChange,
  validateSearch: zodValidator(
    z.object({
      token: z.string().optional(),
    }),
  ),
});

function VerifyEmailChange() {
  const {t} = useTranslation(["common", "verify_account"]);

  useTitle(t("verify_account:email_verification"));

  const {token} = Route.useSearch();

  const {mutate, status} = useMutation({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationKey: USER_KEYS.mUserEmailChangeVerification,
    mutationFn: () => client.post(USER_KEYS.mUserEmailChangeVerification[0], {token}),
  });

  useEffectOnce(() => {
    mutate();
  });

  if (status === "pending") {
    return <FullPageLoader />;
  }

  if (status === "success") {
    return (
      <div className="flex min-h-[calc(100vh-5rem)] flex-col items-center justify-center p-10">
        <div className="border-neutral-grey-light rounded-2.5xl flex w-full max-w-2xl flex-col gap-8 border px-10 py-16 md:px-16">
          <div className="flex flex-col items-center gap-10 text-center">
            <div className="flex flex-col items-center gap-6">
              <div className="bg-success-light-green flex size-20 items-center justify-center rounded-full">
                <CircleWithTickIcon className="text-success-herbal size-11" />
              </div>
              <span className="text-primary-midnight-dark text-3xl font-bold sm:text-4xl">
                {t("email_changed_successfully")}
              </span>
            </div>
            <span className="text-neutral-grey-dark sm:text-lg">{t("email_changed_description")}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-5rem)] flex-col items-center justify-center p-10">
      <div className="border-neutral-grey-light rounded-2.5xl flex w-full max-w-2xl flex-col gap-8 border px-10 py-16 md:px-16">
        <div className="flex flex-col items-center gap-10 text-center">
          <div className="flex flex-col items-center gap-6">
            <div className="bg-danger-light-red flex size-20 items-center justify-center rounded-full">
              <CircleWithTimesIcon className="text-danger-raspberry size-11" />
            </div>
            <span className="text-primary-midnight-dark text-3xl font-bold sm:text-4xl">
              {t("email_could_not_be_verified")}
            </span>
          </div>
          <span className="text-neutral-grey-dark sm:text-lg">{t("email_could_not_be_verified_description")}</span>
        </div>
      </div>
    </div>
  );
}
